import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Topbar from '../../mini-comp/Topbar'
import Sidebar from '../../mini-comp/Sidebar'
import axios from 'axios'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../../config/api'
import {
    FaSpinner,
    FaSearch,
    FaFilter,
    FaFileCsv,
    FaRedo,
    FaUsers,
    FaMoneyBillWave,
    FaCalculator,
    FaBuilding
} from 'react-icons/fa'
import { RiMoneyCnyBoxLine } from 'react-icons/ri'

const NSSAReturns = () => {
    const navigate = useNavigate()

    // Helper to compute default period format YYMM (e.g., "2608" for Aug 2026)
    const getDefaultPeriod = () => {
        const d = new Date()
        const yy = String(d.getFullYear()).slice(-2)
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        return `${yy}${mm}`
    }

    // State management
    const [nssaReturns, setNssaReturns] = useState([])
    const [payrollRuns, setPayrollRuns] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Filters
    const [period, setPeriod] = useState(getDefaultPeriod())
    const [referencePayrollRun, setReferencePayrollRun] = useState('')
    const [ssnSearch, setSsnSearch] = useState('')

    // Currency from cookie
    const currency = Cookies.get('currency') || 'USD'

    // Fetch Payroll Runs list for dropdown
    useEffect(() => {
        const fetchPayrollRuns = async () => {
            try {
                const token = Cookies.get('token')
                const response = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                setPayrollRuns(response.data || [])
            } catch (err) {
                console.error('Error fetching payroll runs:', err)
            }
        }
        fetchPayrollRuns()
    }, [])

    // Fetch NSSA Returns from backend using query params period and reference_payroll_run
    const fetchNSSAReturns = async () => {
        setLoading(true)
        setError(null)
        try {
            const token = Cookies.get('token')
            const params = {}
            if (period) params.period = period
            if (referencePayrollRun) params.reference_payroll_run = referencePayrollRun

            const response = await axios.get(`${API_BASE_URL}/api/v1/payroll/nssa-returns/`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            })
            setNssaReturns(response.data || [])
        } catch (err) {
            console.error('Error fetching NSSA returns:', err)
            setError('Failed to load NSSA Returns data.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchNSSAReturns()
    }, [period, referencePayrollRun])

    // Local filtering by SSN / SSR Number / National ID / Name
    const filteredReturns = useMemo(() => {
        if (!ssnSearch.trim()) return nssaReturns
        const searchLower = ssnSearch.toLowerCase().trim()
        return nssaReturns.filter((item) => {
            const ssr = (item.ssr_number || '').toLowerCase()
            const nationalId = (item.national_id || '').toLowerCase()
            const fullName = (item.full_name || '').toLowerCase()
            const worksNo = (item.works_numbers || '').toLowerCase()
            return (
                ssr.includes(searchLower) ||
                nationalId.includes(searchLower) ||
                fullName.includes(searchLower) ||
                worksNo.includes(searchLower)
            )
        })
    }, [nssaReturns, ssnSearch])

    // Calculate totals
    const totals = useMemo(() => {
        return filteredReturns.reduce(
            (acc, item) => {
                acc.pobsInsurable += parseFloat(item.pobs_insurable_earning || 0)
                acc.pobsContributions += parseFloat(item.pobs_contributions || 0)
                acc.apwcs += parseFloat(item.basic_apwcs_contribution || 0)
                acc.actualInsurable += parseFloat(item.actual_insurable_earnings || 0)
                return acc
            },
            { pobsInsurable: 0, pobsContributions: 0, apwcs: 0, actualInsurable: 0 }
        )
    }, [filteredReturns])

    // Export to CSV
    const handleExportCSV = () => {
        if (filteredReturns.length === 0) return

        const headers = [
            'SSRNumber',
            'SSNNumber',
            'WorksNumber',
            'NationalIDNumber',
            'Period',
            'BirthDate',
            'Surname',
            'FirstName',
            'StartDate',
            'EndDate',
            'POBSInsurableEarning',
            'POBSContributions',
            'BasicAPWCS',
            'ActualInsurableEarnings',
        ]

        const cleanCSVValue = (val) => {
            if (val === null || val === undefined) return ''
            const str = String(val).trim()
            if (
                str === '#' ||
                str === '-' ||
                str === 'None' ||
                str === 'null' ||
                str === 'undefined'
            ) {
                return ''
            }
            if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
                return `"${str.replace(/"/g, '""')}"`
            }
            return str
        }

        const rows = filteredReturns.map((item) => [
            cleanCSVValue(item.ssr_number),
            cleanCSVValue(item.ssn_number),
            cleanCSVValue(item.works_numbers),
            cleanCSVValue(item.national_id),
            cleanCSVValue(item.period),
            cleanCSVValue(item.birth_date),
            cleanCSVValue(item.surname),
            cleanCSVValue(item.first_name),
            cleanCSVValue(item.start_date),
            cleanCSVValue(item.end_date),
            cleanCSVValue(item.pobs_insurable_earning),
            cleanCSVValue(item.pobs_contributions),
            cleanCSVValue(item.basic_apwcs_contribution),
            cleanCSVValue(item.actual_insurable_earnings),
        ])

        const csvString = [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n')

        const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `NSSA_Returns_${period || 'all'}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    // Reset filters
    const handleResetFilters = () => {
        setPeriod(getDefaultPeriod())
        setReferencePayrollRun('')
        setSsnSearch('')
    }

    // Format currency helper
    const formatCurrency = (val) => {
        const amount = parseFloat(val || 0)
        return `${currency} ${amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`
    }

    // Format month label for payroll run option dropdown
    const formatRunMonth = (monthStr) => {
        if (!monthStr) return ''
        const d = new Date(monthStr)
        return d.toLocaleString('en-US', { month: 'long', year: 'numeric' })
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            <Topbar />
            <Sidebar />
            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        {/* Header Section */}
                        <div className="row mb-4 align-items-center">
                            <div className="col-md-8">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="p-3 bg-primary bg-opacity-10 text-primary rounded-3 d-flex align-items-center justify-content-center"
                                        style={{ width: '54px', height: '54px' }}
                                    >
                                        <RiMoneyCnyBoxLine className="fs-24" />
                                    </div>
                                    <div>
                                        <h1 className="page-title fs-24 lh-1 mb-1 fw-bold text-dark">
                                            NSSA Returns
                                        </h1>
                                        <p className="subtitle text-muted mb-0">
                                            National Social Security Authority Monthly Return Reports
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                <button
                                    className="btn btn-outline-secondary me-2 shadow-sm"
                                    onClick={fetchNSSAReturns}
                                    disabled={loading}
                                >
                                    <FaRedo className={loading ? 'spin me-1' : 'me-1'} /> Refresh
                                </button>
                                <button
                                    className="btn btn-success shadow-sm"
                                    onClick={handleExportCSV}
                                    disabled={filteredReturns.length === 0}
                                >
                                    <FaFileCsv className="me-1" /> Export CSV
                                </button>
                            </div>
                        </div>

                        {/* Summary KPI Cards */}
                        <div className="row g-3 mb-4">
                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm rounded-3 h-100">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <span className="text-muted fs-13 fw-medium d-block mb-1">
                                                    Total Employees
                                                </span>
                                                <h4 className="fw-bold text-dark mb-0">
                                                    {filteredReturns.length}
                                                </h4>
                                            </div>
                                            <div className="p-3 bg-primary bg-opacity-10 text-primary rounded-circle">
                                                <FaUsers className="fs-20" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm rounded-3 h-100">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <span className="text-muted fs-13 fw-medium d-block mb-1">
                                                    POBS Insurable Total
                                                </span>
                                                <h5 className="fw-bold text-dark mb-0">
                                                    {formatCurrency(totals.pobsInsurable)}
                                                </h5>
                                            </div>
                                            <div className="p-3 bg-info bg-opacity-10 text-info rounded-circle">
                                                <FaMoneyBillWave className="fs-20" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm rounded-3 h-100">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <span className="text-muted fs-13 fw-medium d-block mb-1">
                                                    POBS Contributions (9%)
                                                </span>
                                                <h5 className="fw-bold text-success mb-0">
                                                    {formatCurrency(totals.pobsContributions)}
                                                </h5>
                                            </div>
                                            <div className="p-3 bg-success bg-opacity-10 text-success rounded-circle">
                                                <FaCalculator className="fs-20" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm rounded-3 h-100">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <span className="text-muted fs-13 fw-medium d-block mb-1">
                                                    APWCS Contribution
                                                </span>
                                                <h5 className="fw-bold text-warning mb-0">
                                                    {formatCurrency(totals.apwcs)}
                                                </h5>
                                            </div>
                                            <div className="p-3 bg-warning bg-opacity-10 text-warning rounded-circle">
                                                <FaBuilding className="fs-20" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Filter Section Card */}
                        <div className="card border-0 shadow-sm mb-4 rounded-3">
                            <div className="card-body p-3">
                                <div className="row g-3 align-items-end">
                                    {/* Period Query Param Filter */}
                                    <div className="col-md-4">
                                        <label className="form-label text-dark fw-semibold fs-13 mb-1">
                                            Period (YYMM)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <FaFilter className="text-muted" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. 2608"
                                                value={period}
                                                onChange={(e) => setPeriod(e.target.value)}
                                            />
                                        </div>
                                        <small className="text-muted fs-11">
                                            Format YYMM (Default current: {getDefaultPeriod()})
                                        </small>
                                    </div>

                                    {/* SSN / SSR Number Local Search Filter */}
                                    <div className="col-md-6">
                                        <label className="form-label text-dark fw-semibold fs-13 mb-1">
                                            SSN / SSR Number Search
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <FaSearch className="text-muted" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search SSN, National ID..."
                                                value={ssnSearch}
                                                onChange={(e) => setSsnSearch(e.target.value)}
                                            />
                                        </div>
                                        <small className="text-muted fs-11">
                                            Client-side SSN / ID search
                                        </small>
                                    </div>

                                    <div className="col-md-2">
                                        {/* Actions */}
                                        <button
                                            className="btn btn-outline-danger w-100"
                                            onClick={handleResetFilters}
                                            title="Reset Filters"
                                        >
                                            Reset
                                        </button>
                                        <small className="text-muted fs-11">
                                            Reset All Filters
                                        </small>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Returns Data Table */}
                        <div className="card border-0 shadow-sm rounded-3">
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <FaSpinner className="spin text-primary fs-28 mb-3" />
                                        <p className="text-muted fw-medium mb-0">
                                            Loading NSSA Return records...
                                        </p>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-5">
                                        <p className="text-danger fw-semibold mb-3">{error}</p>
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchNSSAReturns}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13">
                                                        SSR Number
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13">
                                                        Employee
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13">
                                                        National ID
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13">
                                                        Period
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13 text-end">
                                                        POBS Insurable
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13 text-end">
                                                        POBS Contrib (9%)
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13 text-end">
                                                        APWCS Contrib
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13 text-end">
                                                        Actual Insurable
                                                    </th>
                                                    <th className="px-3 py-3 font-semibold text-dark fs-13">
                                                        Payroll Run Month
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredReturns.length === 0 ? (
                                                    <tr>
                                                        <td
                                                            colSpan="9"
                                                            className="text-center py-5 text-muted fs-14"
                                                        >
                                                            No NSSA Return records found for the selected filters.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredReturns.map((row) => (
                                                        <tr key={row.id}>
                                                            <td className="px-3 py-3">
                                                                <span className="fw-bold text-dark fs-13">
                                                                    {row.ssr_number || '-'}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-3">
                                                                <div>
                                                                    <span className="fw-semibold text-dark d-block fs-13">
                                                                        {row.full_name || `${row.first_name} ${row.surname}`}
                                                                    </span>
                                                                    {row.works_numbers && (
                                                                        <small className="text-muted fs-11">
                                                                            Works #: {row.works_numbers}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-3 text-muted fs-13">
                                                                {row.national_id || '-'}
                                                            </td>
                                                            <td className="px-3 py-3">
                                                                <span className="badge bg-secondary bg-opacity-10 text-secondary fw-semibold">
                                                                    {row.period}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-3 text-end fw-medium text-dark fs-13">
                                                                {formatCurrency(row.pobs_insurable_earning)}
                                                            </td>
                                                            <td className="px-3 py-3 text-end fw-bold text-success fs-13">
                                                                {formatCurrency(row.pobs_contributions)}
                                                            </td>
                                                            <td className="px-3 py-3 text-end fw-medium text-warning fs-13">
                                                                {formatCurrency(row.basic_apwcs_contribution)}
                                                            </td>
                                                            <td className="px-3 py-3 text-end text-muted fs-13">
                                                                {formatCurrency(row.actual_insurable_earnings)}
                                                            </td>
                                                            <td className="px-3 py-3 text-muted fs-13">
                                                                {row.reference_payroll_run_month
                                                                    ? formatRunMonth(row.reference_payroll_run_month)
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {filteredReturns.length > 0 && (
                                                <tfoot className="bg-light fw-bold">
                                                    <tr>
                                                        <td colSpan="4" className="px-3 py-3 text-end text-dark">
                                                            Total ({filteredReturns.length} records):
                                                        </td>
                                                        <td className="px-3 py-3 text-end text-dark fs-13">
                                                            {formatCurrency(totals.pobsInsurable)}
                                                        </td>
                                                        <td className="px-3 py-3 text-end text-success fs-13">
                                                            {formatCurrency(totals.pobsContributions)}
                                                        </td>
                                                        <td className="px-3 py-3 text-end text-warning fs-13">
                                                            {formatCurrency(totals.apwcs)}
                                                        </td>
                                                        <td className="px-3 py-3 text-end text-muted fs-13">
                                                            {formatCurrency(totals.actualInsurable)}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default NSSAReturns